/**
 * THE DOCUMENT AND FOLDER WIRE SHAPES, AND THE CLASSIFICATION VOCABULARY.
 *
 * Library LB3-T1 (decision 19: a wiki article is a document). principles.md #1
 * ("one store, one resolver, one owner per noun") and #3 ("nothing that
 * describes a noun is hardcoded").
 *
 * WHAT THIS FILE IS THE ONLY DECLARATION OF
 *   - the sensitivity alphabet (`unclassified | cui | restricted`) and the
 *     export-control alphabet (`none | itar | ear`). Before this file they were
 *     written out in more than sixty places across api, web and shared — Zod
 *     enums, TypeScript unions, Drizzle pgEnums, ordering ladders — and one
 *     of them spelled export control in upper case. Every one of those now
 *     imports from here. `__tests__/shared-document-schemas.test.ts` fails on
 *     a literal re-declaration anywhere else (the H8 allowlist names the one
 *     vocabulary deliberately left alone: agent memory's stored upper-case
 *     `ITAR`/`EAR`, held for the founder);
 *   - a document's kind (`file | article | synced`), its ingestion status (the
 *     `document_status` enum, `pending` included) and an article's state
 *     (`knowledge_articles.status` as its CHECK constraint declares it);
 *   - the Document and Folder shapes the API serves and the web renders.
 *
 * WHAT IT DOES NOT DECIDE. Who may see what is the ONE clearance rule
 * (`packages/api/src/policy/clearance.ts`, LB3-T2) and the ONE visibility
 * resolver (LB-4). This file names the values; it holds no rule over them.
 * The two ORDERS below are facts about the vocabulary that several readers
 * already relied on (each kept a private copy) — "the strictest
 * classification of a set" — not an access decision.
 *
 * Folder classification (C1, open) is stored and echoed exactly as today:
 * `FolderSchema` carries the two columns; nothing reads them to decide.
 */

import { z } from 'zod';

// ─── The classification vocabulary ──────────────────────────────────────────

/**
 * The sensitivity alphabet, in ascending order of strictness
 * (unclassified < cui < restricted). The order is load-bearing: readers that
 * take "the highest of several" index into this array.
 */
export const SENSITIVITIES = Object.freeze(['unclassified', 'cui', 'restricted'] as const);
export const SensitivitySchema = z.enum(SENSITIVITIES);
export type Sensitivity = z.infer<typeof SensitivitySchema>;

/** The export-control alphabet, in the order the screens offer it. */
export const EXPORT_CONTROLS = Object.freeze(['none', 'itar', 'ear'] as const);
export const ExportControlSchema = z.enum(EXPORT_CONTROLS);
export type ExportControl = z.infer<typeof ExportControlSchema>;

/**
 * Export control in ascending order of strictness (none < ear < itar) — the
 * lattice the response-classification roll-ups climb ("a response inherits
 * the highest source classification on both axes"). The same three values as
 * `EXPORT_CONTROLS`, ordered by strictness rather than by how a picker lists
 * them; the test holds them to the same set.
 *
 * Every array in this file is FROZEN: one object is shared by reference by
 * every reader, so a stray `.sort()` / `.reverse()` / `.push()` would reorder
 * the platform-wide strictness order for the life of the process. Frozen, it
 * throws instead (LB3-T1 review BF-09).
 */
export const EXPORT_CONTROLS_BY_STRICTNESS = Object.freeze(
  ['none', 'ear', 'itar'] as const,
) satisfies readonly ExportControl[];

/**
 * A value's rank in the strictness order — its index in `SENSITIVITIES` /
 * `EXPORT_CONTROLS_BY_STRICTNESS` — or **-1 when the value is not in the
 * alphabet** (an unknown value, an upper-case spelling, `undefined`). A
 * caller treats -1 as deny, or as the strictest rank when it rolls up; it
 * never treats it as the floor (principles.md #4, nothing fails open). This
 * is the only rank: a private `{ unclassified: 0, cui: 1, restricted: 2 }`
 * ladder is a second copy of the order and the guard test fails on one
 * (LB3-T1 review R4 / BF-07).
 */
export function sensitivityRank(value: unknown): number {
  return typeof value === 'string' ? (SENSITIVITIES as readonly string[]).indexOf(value) : -1;
}

/** See `sensitivityRank`: none < ear < itar, -1 for anything else. */
export function exportControlRank(value: unknown): number {
  return typeof value === 'string'
    ? (EXPORT_CONTROLS_BY_STRICTNESS as readonly string[]).indexOf(value)
    : -1;
}

/** The strictest value of each axis — what an unknown value rolls up to. */
export const STRICTEST_SENSITIVITY: Sensitivity = SENSITIVITIES[SENSITIVITIES.length - 1];
export const STRICTEST_EXPORT_CONTROL: ExportControl =
  EXPORT_CONTROLS_BY_STRICTNESS[EXPORT_CONTROLS_BY_STRICTNESS.length - 1];

/**
 * The words a person reads for each value — the ONE label table: the web's
 * selects, badges and restore confirm, and the api's audit summaries, all
 * name a value through it (LB6cT2-REV-03: an audit line names "CUI / ITAR",
 * never the stored keys).
 */
export const SENSITIVITY_LABELS: Readonly<Record<Sensitivity, string>> = Object.freeze({
  unclassified: 'Unclassified',
  cui: 'CUI',
  restricted: 'Restricted',
});

export const EXPORT_CONTROL_LABELS: Readonly<Record<ExportControl, string>> = Object.freeze({
  none: 'None',
  itar: 'ITAR',
  ear: 'EAR',
});

/** The label of a sensitivity value; an unknown value is shown as itself. */
export function sensitivityLabel(value: string): string {
  return (SENSITIVITY_LABELS as Record<string, string>)[value] ?? value;
}

/** The label of an export-control value; an unknown value is shown as itself. */
export function exportControlLabel(value: string): string {
  return (EXPORT_CONTROL_LABELS as Record<string, string>)[value] ?? value;
}

/** The two classification axes, as a restore's answer names the ones that rose. */
export const CLASSIFICATION_AXES = Object.freeze(['sensitivity', 'exportControl'] as const);
export const ClassificationAxisSchema = z.enum(CLASSIFICATION_AXES);
export type ClassificationAxis = z.infer<typeof ClassificationAxisSchema>;

/**
 * The strictest sensitivity of a set — "a response inherits the highest
 * source classification". A value outside the alphabet counts as the
 * STRICTEST, never as nothing; an empty set is the floor. A caller that means
 * "no tag = unclassified" says so before calling (`v ?? 'unclassified'`).
 */
export function strictestSensitivity(values: Iterable<unknown>): Sensitivity {
  let max = 0;
  for (const v of values) {
    const r = sensitivityRank(v);
    max = Math.max(max, r < 0 ? SENSITIVITIES.length - 1 : r);
  }
  return SENSITIVITIES[max];
}

/** See `strictestSensitivity`: none < ear < itar; unknown counts as itar. */
export function strictestExportControl(values: Iterable<unknown>): ExportControl {
  let max = 0;
  for (const v of values) {
    const r = exportControlRank(v);
    max = Math.max(max, r < 0 ? EXPORT_CONTROLS_BY_STRICTNESS.length - 1 : r);
  }
  return EXPORT_CONTROLS_BY_STRICTNESS[max];
}

// ─── Document vocabularies ──────────────────────────────────────────────────

/**
 * What a document IS (decision 19). `file` — uploaded; `article` — a wiki
 * article, its body and versions in child tables; `synced` — brought in by a
 * library connector. Stored as `ai_documents.kind` from c41 (LB3-T3).
 */
export const DOCUMENT_KINDS = Object.freeze(['file', 'article', 'synced'] as const);
export const DocumentKindSchema = z.enum(DOCUMENT_KINDS);
export type DocumentKind = z.infer<typeof DocumentKindSchema>;

/**
 * The ingestion status of a document — the `document_status` Postgres enum
 * (`db/schema/enums.ts` builds it from this array).
 */
export const DOCUMENT_STATUSES = Object.freeze(['pending', 'processing', 'ready', 'failed'] as const);
export const DocumentStatusSchema = z.enum(DOCUMENT_STATUSES);
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

/**
 * An article's state — today's `knowledge_articles.status` alphabet as its
 * CHECK constraint declares it (bA7, `chk_knowledge_articles_status`).
 */
export const ARTICLE_STATES = Object.freeze(['draft', 'published', 'deprecated', 'retired'] as const);
export const ArticleStateSchema = z.enum(ARTICLE_STATES);
export type ArticleState = z.infer<typeof ArticleStateSchema>;

// ─── The wire shapes ────────────────────────────────────────────────────────

/**
 * A Library document as the API serves it (GET /knowledge/documents and the
 * single-document routes). `documentType` stays an open string: the document
 * type catalogue is open question C4 and keeps today's behaviour.
 * `classification` is the legacy free-text column, echoed as today.
 */
export const DocumentSchema = z.object({
  id: z.string(),
  kind: DocumentKindSchema,
  title: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  fileSize: z.number(),
  status: DocumentStatusSchema,
  documentType: z.string(),
  description: z.string().nullable(),
  classification: z.string().nullable(),
  sensitivity: SensitivitySchema,
  exportControl: ExportControlSchema,
  processingError: z.string().nullable(),
  chunkCount: z.number(),
  folderId: z.string().nullable(),
  uploadedBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * An article's state (the one article state alphabet) — null for a file or
   * a synced document, whose `status` is its ingestion status. The Documents
   * page lists every kind in ONE answer (Library LB6-T2, L6-15) and shows an
   * article's state and its Publish / Unpublish from this field.
   */
  articleState: ArticleStateSchema.nullable(),
});
export type DocumentShape = z.infer<typeof DocumentSchema>;

/**
 * The orders a document list may be sorted in (GET /knowledge/documents and
 * its neighbours, `sort` / `sortDir`). Declared here so the API's query
 * schema and the Documents page's sortable columns read ONE list (Library
 * LB6-T2): a column the server cannot order by is not offered as sortable.
 */
export const DOCUMENT_LIST_SORTS = Object.freeze(['createdAt', 'updatedAt', 'title'] as const);
export const DocumentListSortSchema = z.enum(DOCUMENT_LIST_SORTS);
export type DocumentListSort = z.infer<typeof DocumentListSortSchema>;
/**
 * The longest title search a document list takes (GET /knowledge/documents and
 * its neighbours, `search`). Declared once: the API's query schema rejects a
 * longer one, and the Documents page's search box stops there, so a person
 * can never type a search the server answers with a 400 (LB6T2-BUG-07).
 */
export const DOCUMENT_SEARCH_MAX_LENGTH = 500;
export const DOCUMENT_SORT_DIRECTIONS = Object.freeze(['asc', 'desc'] as const);
export const DocumentSortDirectionSchema = z.enum(DOCUMENT_SORT_DIRECTIONS);
export type DocumentSortDirection = z.infer<typeof DocumentSortDirectionSchema>;

/**
 * How many documents of each kind one list answer covers — the same WHERE as
 * the list (visibility, folder, kind, status, search) without LIMIT. The
 * Documents page's "N items · A articles · F uploaded · S synced" line is
 * this answer, never a tally of the loaded page (Library LB6-T2, L6-15).
 */
export const DocumentKindCountsSchema = z.record(DocumentKindSchema, z.number().int().nonnegative());
export type DocumentKindCounts = z.infer<typeof DocumentKindCountsSchema>;

/** GET /knowledge/documents — a page of the documents this person may see, and how many there are. */
export const DocumentListResponseSchema = z.object({
  data: z.array(DocumentSchema),
  total: z.number(),
  countsByKind: DocumentKindCountsSchema,
});
export type DocumentListResponse = z.infer<typeof DocumentListResponseSchema>;

/** A document named by id and title — a neighbour in a list. */
export const DocumentRefSchema = z.object({ id: z.string(), title: z.string() });
export type DocumentRef = z.infer<typeof DocumentRefSchema>;

/**
 * GET /knowledge/documents/:id/neighbours — the documents before and after
 * this one in the list it was opened from, under the SAME visibility
 * predicate, filters and order as GET /knowledge/documents (L6-16). Null at
 * either end of the list.
 */
export const DocumentNeighboursSchema = z.object({
  prev: DocumentRefSchema.nullable(),
  next: DocumentRefSchema.nullable(),
});
export type DocumentNeighbours = z.infer<typeof DocumentNeighboursSchema>;

/** A tag as the routes serve it on any document (GET /wiki/tags, every article row, GET /knowledge/documents/:id). */
export const ArticleTagSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
  sortOrder: z.number(),
});
export type ArticleTagShape = z.infer<typeof ArticleTagSchema>;

/**
 * A tag as the tag list serves it (GET /wiki/tags): the tag, and how many of
 * the documents this person may see carry it — the Document visibility
 * resolver's own predicate, counted by the server (Library LB6-T2: a count is
 * an answer, never a tally of whichever articles the browser loaded).
 */
export const TagListItemSchema = ArticleTagSchema.extend({
  count: z.number().int().nonnegative(),
});
export type TagListItemShape = z.infer<typeof TagListItemSchema>;

/**
 * The body of PUT /knowledge/documents/:id/tags — the ONE door that writes a
 * document's tags, for every kind (decision 60, LB6c-T1): the whole set, by
 * tag id (an empty list clears it). Strict: an unknown key is refused.
 */
export const DocumentTagsWriteSchema = z
  .object({
    tags: z.array(z.string().uuid()).max(100),
  })
  .strict();
export type DocumentTagsWrite = z.infer<typeof DocumentTagsWriteSchema>;

/**
 * A document's tags as stored (the answer of PUT /knowledge/documents/:id/tags,
 * and the `tags` of GET /knowledge/documents/:id): sort order, then name.
 */
export const DocumentTagsSchema = z.object({
  tags: z.array(ArticleTagSchema),
});
export type DocumentTags = z.infer<typeof DocumentTagsSchema>;

/**
 * A wiki article as the API serves it in a list (GET /wiki/articles). An
 * article IS a document (decision 19): the fields it shares with one are the
 * Document shape's, picked from it; its state is the one article state
 * alphabet (ArticleStateSchema — the store's `publish_status` CHECK, bA7). The
 * rest are the article's own. Declared once here and read by both the API
 * route and the web row type (LB3T5-R-02 — the route's private copy and two
 * web hand copies had drifted: the web said the state alphabet, the wire an
 * open string).
 */
export const ArticleSchema = DocumentSchema.pick({
  id: true,
  title: true,
  folderId: true,
  sensitivity: true,
  exportControl: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  slug: z.string().nullable(),
  excerpt: z.string().nullable(),
  parentId: z.string().nullable(),
  status: ArticleStateSchema,
  version: z.number(),
  // Null once the author's account is hard-deleted (c41: the article outlives it).
  authorId: z.string().nullable(),
  authorName: z.string().nullable(),
  publishedAt: z.string().nullable(),
  viewCount: z.number(),
  tags: z.array(ArticleTagSchema),
});
export type ArticleShape = z.infer<typeof ArticleSchema>;

/** An article with its body (GET /wiki/articles/:id, and the create / update answers). */
export const ArticleDetailSchema = ArticleSchema.extend({
  content: z.string(),
  contentText: z.string(),
});
export type ArticleDetailShape = z.infer<typeof ArticleDetailSchema>;

/**
 * One saved version of an article as the API lists it (GET
 * /wiki/articles/:id/versions, newest first) — a row of the article's
 * versions child table, `document_article_versions` (decision 19). Declared
 * once here and read by both the route and the reader's History panel
 * (decision 39 / C3, Library LB6-T9).
 */
export const ArticleVersionSchema = z.object({
  id: z.string(),
  articleId: z.string(),
  versionNumber: z.number(),
  title: z.string(),
  excerpt: z.string().nullable(),
  editedBy: z.string().nullable(),
  editedByName: z.string().nullable(),
  editedAt: z.string(),
  editSummary: z.string().nullable(),
});
export type ArticleVersionShape = z.infer<typeof ArticleVersionSchema>;

/**
 * The versions list is read a page at a time, newest first (review
 * LB6T9-BF-04: an article saved thousands of times must not answer thousands
 * of rows on every History open). `limit` defaults to
 * ARTICLE_VERSIONS_PAGE_DEFAULT and never exceeds ARTICLE_VERSIONS_PAGE_MAX.
 */
export const ARTICLE_VERSIONS_PAGE_DEFAULT = 50;
export const ARTICLE_VERSIONS_PAGE_MAX = 100;

/** GET /wiki/articles/:id/versions?before=&limit= — the versions numbered below `before`. */
export const ArticleVersionListQuerySchema = z.object({
  before: z.coerce.number().int().positive().max(2_147_483_647).optional(),
  limit: z.coerce.number().int().min(1).max(ARTICLE_VERSIONS_PAGE_MAX).optional(),
});
export type ArticleVersionListQuery = z.infer<typeof ArticleVersionListQuerySchema>;

/**
 * One page of the versions list. `nextBefore` is the `before` of the next
 * (older) page, or null when this page holds the oldest version.
 */
export const ArticleVersionPageSchema = z.object({
  data: z.array(ArticleVersionSchema),
  nextBefore: z.number().nullable(),
});
export type ArticleVersionPage = z.infer<typeof ArticleVersionPageSchema>;

/**
 * Decision 61 — the article labels a version restore raises to (the strictest,
 * per axis, of the article's own and the version's). Declared once; the
 * version detail and the restore answer both carry it. The server computes it
 * (document-writer.ts `restoreClassification`); the web only reads it.
 * `raisedAxes` names the axes that actually rise (LB6cT2-REV-01) — the
 * other axis keeps the article's own value — so a reader names only what
 * changes and never works it out itself.
 */
export const ClassificationRaiseSchema = z.object({
  sensitivity: SensitivitySchema,
  exportControl: ExportControlSchema,
  raisedAxes: z.array(ClassificationAxisSchema).min(1),
});
export type ClassificationRaise = z.infer<typeof ClassificationRaiseSchema>;

/**
 * One version with its text (GET /wiki/articles/:id/versions/:versionNumber).
 * `restoreRaisesClassificationTo` is what restoring it would raise the
 * article's labels to — null when nothing would rise (decision 61).
 */
export const ArticleVersionDetailSchema = ArticleVersionSchema.extend({
  content: z.string(),
  contentText: z.string().nullable(),
  restoreRaisesClassificationTo: ClassificationRaiseSchema.nullable(),
});
export type ArticleVersionDetailShape = z.infer<typeof ArticleVersionDetailSchema>;

/**
 * The answer of POST /wiki/articles/:id/versions/:versionNumber/restore: the
 * current text was snapshotted as a new version first, so the restore itself
 * can be undone from the same History.
 *
 * `changed` is false when the article already held that version's text: no
 * version was saved and nothing was written (a published article is still
 * re-indexed — the retry the 502 RAG_INGEST_FAILED answer asks for).
 * `savedAsVersion` is the version number the replaced text was saved as —
 * a number of the versions list — or null when nothing changed (review
 * LB6T9-BF-01 / BF-03). `newCurrentVersion` is the ARTICLE's own counter
 * (every save bumps it), a different numbering from the versions list.
 */
export const ArticleVersionRestoreResultSchema = z.object({
  success: z.boolean(),
  restoredFromVersion: z.number(),
  newCurrentVersion: z.number(),
  changed: z.boolean(),
  savedAsVersion: z.number().nullable(),
  /** Decision 61: the labels the restore raised the article to; null when none rose. */
  classificationRaisedTo: ClassificationRaiseSchema.nullable(),
});
export type ArticleVersionRestoreResult = z.infer<typeof ArticleVersionRestoreResultSchema>;

/**
 * A Library folder as the API serves it (GET /wiki/folders and the folder
 * writes) — a row of the one folder store, `ai_document_folders` (decision
 * 18). `sensitivity` / `exportControl` are the C1 columns: a FLOOR over every
 * document beneath the folder (decision 37), applied when visibility is read
 * (api policy/document-visibility.ts) — the documents' own stored values are
 * not rewritten. It holds on a surface once that surface's reader asks the
 * resolver (Library LB-4); the older allow-list path does not apply it.
 * The timestamps are nullable because the store declares them so.
 */
export const FolderSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string().nullable(),
  description: z.string().nullable(),
  parentId: z.string().nullable(),
  sortOrder: z.number(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  sensitivity: SensitivitySchema,
  exportControl: ExportControlSchema,
  isDefault: z.boolean(),
  /**
   * How many documents of every kind (files, synced documents, articles) the
   * person asking may see directly in this folder — the Document visibility
   * resolver's own predicate (browse), so the count equals what the lists
   * show them. 0 on a write's answer.
   */
  count: z.number().int().nonnegative(),
  createdBy: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type FolderShape = z.infer<typeof FolderSchema>;

/**
 * ONE ENTRY OF THE DOCUMENT-TYPE CATALOGUE, as the wire carries it (Library
 * LB6-T10, decision 40 / C4; review LB6T10-R2) — declared once, here. Both
 * catalogue routes answer it (GET /knowledge/document-types: the built-ins +
 * the tenant's own; GET /admin/platform/library/document-types: the
 * built-ins), the api's catalogue module returns it, and the web's pickers,
 * badge and Manage Types dialog read it. `name` is the KEY a document stores
 * in ai_documents.document_type; `label` is what a person sees.
 */
export const DocumentTypeEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  isBuiltin: z.boolean(),
});
export type DocumentTypeEntry = z.infer<typeof DocumentTypeEntrySchema>;

/** The answer of both catalogue routes: `{ data: DocumentTypeEntry[] }`. */
export const DocumentTypeListResponseSchema = z.object({
  data: z.array(DocumentTypeEntrySchema),
});
export type DocumentTypeListResponse = z.infer<typeof DocumentTypeListResponseSchema>;
